import { IAuthDTO, ITempAuthDTO } from "../data-access/models/response/auth";
import moment from "moment";
import { CommonHelper } from "./common-helper";
import { UserRole } from "src/data-access/models/enums";
import { SALES_TRIGGER_LOCAL_STOARGE_KEY } from "src/components/users/home";
import { IAIChatMessage } from "src/data-access/models/request/ai-generate";

const LOCAL_STORAGE_AUTH_KEY: string = "auth";
const LOCAL_STORAGE_GUEST_AUTH_KEY: string = "guestauth";
const LOCAL_STORAGE_CHATBOT_CHAT_KEY: string = "evachat";

export const AuthStore = {
    getAccessToken: (): string | undefined => {
        try {
            const authStr = localStorage.getItem(LOCAL_STORAGE_AUTH_KEY);
            if (authStr) {
                const authDto = JSON.parse(CommonHelper.decrypt(authStr)!) as IAuthDTO;
                return authDto.accessToken;
            }

            const guestAuthStr = localStorage.getItem(LOCAL_STORAGE_GUEST_AUTH_KEY);
            if (guestAuthStr) {
                const guestAuthDto = JSON.parse(CommonHelper.decrypt(guestAuthStr)!) as ITempAuthDTO;
                return guestAuthDto.accessToken;
            }
        } catch {
            // Handle any decryption or parsing errors gracefully
        }
        return undefined;
    },

    getAccessTokenExpiration: (): string | undefined => {
        try {
            const token = AuthStore.getAccessToken();
            if (!token) {
                return undefined;
            }

            // JWT format: header.payload.signature
            const payloadBase64 = token.split('.')[1];
            if (!payloadBase64) {
                return undefined;
            }

            const payloadJson = atob(payloadBase64);
            const payload = JSON.parse(payloadJson);

            if (typeof payload.exp === 'number') {
                return CommonHelper.getDisplayDateTime(new Date(payload.exp * 1000).toISOString());
            }
        } catch {
            // Gracefully handle invalid token format or other errors
        }

        return undefined;
    },

    checkUserAccessTokenValid: (): boolean => {
        try {
            const authStr = localStorage.getItem(LOCAL_STORAGE_AUTH_KEY);
            if (authStr) {
                const authdto = JSON.parse(CommonHelper.decrypt(authStr)!) as IAuthDTO;
                return Math.abs(moment.utc(authdto.accessTokenValidTo).diff(moment.utc(), 'seconds')) > 10;
            }

            const guestAuthStr = localStorage.getItem(LOCAL_STORAGE_GUEST_AUTH_KEY);
            if (guestAuthStr) {
                const authdto = JSON.parse(CommonHelper.decrypt(guestAuthStr)!) as ITempAuthDTO;
                return Math.abs(moment.utc(authdto.accessTokenValidTo).diff(moment.utc(), 'seconds')) > 10;
            }
        } catch {
            // Handle decryption or parsing errors
        }
        return false;
    },

    getAuth: (): IAuthDTO | ITempAuthDTO | undefined => {
        try {
            const authStr = localStorage.getItem(LOCAL_STORAGE_AUTH_KEY);
            if (authStr) {
                const authdto: IAuthDTO = JSON.parse(CommonHelper.decrypt(authStr)!) as IAuthDTO;
                return authdto;
            }

            const guestAuthStr = localStorage.getItem(LOCAL_STORAGE_GUEST_AUTH_KEY);
            if (guestAuthStr) {
                const guestAuthdto: ITempAuthDTO = JSON.parse(CommonHelper.decrypt(guestAuthStr)!) as ITempAuthDTO;
                return guestAuthdto;
            }
        } catch {
            // Handle any decryption or parsing errors gracefully
        }
        return undefined;
    },

    setAuth: (auth: IAuthDTO | ITempAuthDTO): void => {
        const isGuest = (auth as ITempAuthDTO).shareId !== undefined;

        AuthStore.removeAuth();
        if (isGuest)
            localStorage.setItem(LOCAL_STORAGE_GUEST_AUTH_KEY, CommonHelper.encrypt(JSON.stringify(auth)));
        else
            localStorage.setItem(LOCAL_STORAGE_AUTH_KEY, CommonHelper.encrypt(JSON.stringify(auth)));
    },

    removeAuth: (): void => {
        localStorage.removeItem(LOCAL_STORAGE_AUTH_KEY);
        localStorage.removeItem(LOCAL_STORAGE_GUEST_AUTH_KEY);
        localStorage.removeItem(SALES_TRIGGER_LOCAL_STOARGE_KEY);
        localStorage.removeItem(LOCAL_STORAGE_CHATBOT_CHAT_KEY);
    },

    getRole: (): UserRole | undefined => {
        try {
            const authStr = localStorage.getItem(LOCAL_STORAGE_AUTH_KEY);
            if (authStr) {
                const authdto: IAuthDTO = JSON.parse(CommonHelper.decrypt(authStr)!) as IAuthDTO;
                return authdto.role;
            }

            const guestAuthStr = localStorage.getItem(LOCAL_STORAGE_GUEST_AUTH_KEY);
            if (guestAuthStr) {
                return UserRole.GuestUser;
            }
        } catch { }

        return undefined;
    },

    setChatConversation: (messages: IAIChatMessage[]) => {
        localStorage.setItem(LOCAL_STORAGE_CHATBOT_CHAT_KEY, JSON.stringify(messages));
    },

    getChatConversation: (): Array<IAIChatMessage> => {
        var messages = localStorage.getItem(LOCAL_STORAGE_CHATBOT_CHAT_KEY);
        if (messages) {
            try {
                return JSON.parse(messages) as IAIChatMessage[];
            }
            catch { }
        }

        return [];
    },
}
